import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Bell, X, CheckCheck, Trash2, ShoppingCart, AlertTriangle,
  Wallet, Truck, HandCoins, Info,
} from "lucide-react";

const TONE = {
  success: { dot: "bg-green-500", ring: "border-l-green-500", chip: "text-green-700" },
  danger: { dot: "bg-red-500", ring: "border-l-red-500", chip: "text-agri-danger" },
  warning: { dot: "bg-amber-500", ring: "border-l-amber-500", chip: "text-agri-amber-dark" },
  info: { dot: "bg-blue-500", ring: "border-l-blue-500", chip: "text-blue-700" },
};
const ICON = { sale: ShoppingCart, stock: AlertTriangle, payment: Wallet, purchase: Truck, payable: HandCoins, system: Info };

function ago(iso) {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationCenter() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get("/notifications", { params: { limit: 50 } })
      .then((r) => ({ items: r.data, unread: Number(r.headers["x-unread-count"] ?? 0) })),
    refetchInterval: 20000,
  });
  const items = data?.items ?? [];
  const unread = data?.unread ?? 0;

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["notifications"] });
  const markRead = useMutation({ mutationFn: (id) => api.post(`/notifications/${id}/read`), onSuccess: invalidate });
  const readAll = useMutation({ mutationFn: () => api.post("/notifications/read-all"), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id) => api.delete(`/notifications/${id}`), onSuccess: invalidate });
  const clearAll = useMutation({ mutationFn: () => api.delete("/notifications"), onSuccess: invalidate });

  const openItem = (n) => {
    if (!n.read) markRead.mutate(n.id);
    if (n.link) { setOpen(false); nav(n.link); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notif-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg text-agri-ink-2 hover:text-agri-forest hover:bg-black/5 transition-colors"
        title="Notifications"
      >
        <Bell size={19} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 rounded-full bg-agri-danger text-white text-[10px] font-bold grid place-items-center tabular-nums">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[92vw] bg-white rounded-xl shadow-xl border border-[rgba(28,28,30,0.08)] z-50 animate-fade-up overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(28,28,30,0.06)]">
            <div className="font-heading text-base">Notifications {unread > 0 && <span className="text-agri-danger">· {unread} new</span>}</div>
            <div className="flex items-center gap-1">
              <button onClick={() => readAll.mutate()} disabled={!unread} title="Mark all read"
                className="p-1.5 rounded-md text-agri-ink-2 hover:text-agri-forest hover:bg-black/5 disabled:opacity-30"><CheckCheck size={16} /></button>
              <button onClick={() => clearAll.mutate()} disabled={!items.length} title="Clear all"
                className="p-1.5 rounded-md text-agri-ink-2 hover:text-agri-danger hover:bg-red-50 disabled:opacity-30"><Trash2 size={16} /></button>
            </div>
          </div>

          <div className="max-h-[26rem] overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-12 text-center text-sm text-agri-ink-2 flex flex-col items-center gap-2">
                <Bell size={26} className="opacity-30" /> You're all caught up.
              </div>
            )}
            {items.map((n) => {
              const tone = TONE[n.level] || TONE.info;
              const Icon = ICON[n.type] || Info;
              return (
                <div key={n.id}
                  className={`group relative flex gap-3 px-4 py-3 border-b border-[rgba(28,28,30,0.04)] border-l-[3px] ${tone.ring} ${n.read ? "bg-white" : "bg-agri-bg/50"} hover:bg-agri-bg transition-colors ${n.link ? "cursor-pointer" : ""}`}
                  onClick={() => openItem(n)}>
                  <div className={`mt-0.5 h-8 w-8 shrink-0 rounded-full grid place-items-center ${tone.chip} bg-black/[0.03]`}>
                    <Icon size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-2">
                      {!n.read && <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${tone.dot}`} />}
                      <div className="text-sm font-medium text-agri-ink leading-snug">{n.title}</div>
                    </div>
                    {n.message && <div className="text-xs text-agri-ink-2 mt-0.5 tabular-nums">{n.message}</div>}
                    <div className="text-[11px] text-agri-ink-2/70 mt-1">{ago(n.created_at)}</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); remove.mutate(n.id); }}
                    title="Remove"
                    className="opacity-0 group-hover:opacity-100 self-start p-1 rounded text-agri-ink-2 hover:text-agri-danger hover:bg-red-50 transition-opacity"
                  ><X size={14} /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
